import React, { useState, useEffect } from "react";
import { Heart, Users, ShieldCheck, Check, Mail, User, Lock, Gift, Minus, Plus, Loader2, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { HERO_BOOK_IMAGE, heroChecklist, heroFeatures } from "../mock";

const iconMap = { Heart, Users, ShieldCheck };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OPTIONS = {
  book: { id: "book", label: "The Book", price: 30 },
  bundle: { id: "bundle", label: "Book + Workbook", price: 40 },
};

export default function Hero() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [option, setOption] = useState("bundle");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onSelect = (e) => setOption(e.detail);
    window.addEventListener("select-option", onSelect);
    return () => window.removeEventListener("select-option", onSelect);
  }, []);

  const unitPrice = OPTIONS[option].price;
  const total = (unitPrice * quantity).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        name,
        email,
        quantity,
        option,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong starting checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <section id="preorder" className="relative overflow-hidden bg-[#EAE9F6]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 py-10 md:px-8 lg:grid-cols-12 lg:gap-6 lg:py-14">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#A9D9F7] bg-[#DCEFFB] px-4 py-2">
            <Gift className="h-4 w-4 text-[#48B6F7]" />
            <span className="text-xs font-extrabold tracking-[0.12em] text-[#1E5B8A]">
              PRESALE • LIMITED TIME ONLY
            </span>
          </div>

          <h1 className="font-serif-display text-[2.6rem] font-bold leading-[1.08] text-[#6618D0] md:text-[3.1rem]">
            Parenting doesn't come with a guide.{" "}
            <span className="text-[#990EE5]">This is the guide I wish I had.</span>
          </h1>

          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[#4a4a4a]">
            A practical, real-life book for working parents who want to stay calm,
            set boundaries, and raise confident, emotionally resilient kids, without
            pressure, guilt, or burnout.
          </p>

          <div className="mt-9 flex flex-wrap gap-8">
            {heroFeatures.map((f) => {
              const Icon = iconMap[f.icon];
              return (
                <div key={f.label} className="flex items-center gap-3">
                  <Icon className="h-7 w-7 shrink-0 text-[#48B6F7]" strokeWidth={1.8} />
                  <span className="whitespace-pre-line text-[13px] font-extrabold leading-tight text-[#6618D0]">
                    {f.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div data-testid="hero-story" className="mt-9 max-w-md">
            <p className="text-xs font-extrabold tracking-[0.15em] text-[#990EE5]">
              THE STORY BEHIND THE BOOK
            </p>
            <blockquote className="mt-3 rounded-xl border-l-4 border-[#6618D0] bg-white px-5 py-4 shadow-sm">
              <p className="font-serif-display text-[15px] italic leading-relaxed text-[#3A0F7A]">
                "A teacher knocked on my door. She didn't come to complain. She came
                because she wanted to meet the mother behind that child."
              </p>
            </blockquote>
            <div className="mt-3 space-y-3 text-[13px] leading-relaxed text-[#4a4a4a]">
              <p>
                That moment, a teacher wanting to understand how I raised my child,
                is the proof behind everything I teach.
              </p>
              <p>
                I grew up in Ivory Coast, the eldest of five children in a home full
                of love and pressure. My parents gave everything they had, but they
                didn't have the tools. Yelling. Tension. The weight of expectation
                with no language for feelings.
              </p>
              <p>
                I became a teacher. Then I moved to Canada with my husband and four
                children, with no extended family and a career to rebuild from
                scratch. One question became urgent:{" "}
                <em className="text-[#6618D0]">
                  How do I raise resilient, confident children without passing on
                  the pressure I grew up under?
                </em>
              </p>
              <p>
                I found the answer, tested it at home, applied it in my classroom,
                and watched my children thrive. Not because I pushed harder, but
                because I stopped. That answer became{" "}
                <span className="font-bold text-[#6618D0]">the Response-Ready Method</span>,
                and this book is where I share it with you.
              </p>
            </div>
          </div>
        </div>

        {/* Center image */}
        <div className="lg:col-span-4">
          <div data-testid="hero-book-image" className="overflow-hidden rounded-2xl shadow-lg lg:sticky lg:top-24">
            <img
              src={HERO_BOOK_IMAGE}
              alt="The Mother Behind That Child by Rachelle Koulibaly on a table with coffee and flowers"
              className="w-full object-cover transition-transform duration-700 hover:scale-[1.02]"
            />
          </div>
        </div>

        {/* Right order form card */}
        <div className="lg:col-span-3">
          <div className="relative rounded-2xl border border-[#D8CFF0] bg-white p-6 shadow-xl md:p-7">
            <h2 className="font-serif-display text-[1.7rem] font-bold leading-tight text-[#6618D0]">
              Choose how you'll begin
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-[#5a5a5a]">
              The book helps you understand the pattern. The workbook helps you
              work on yours.
            </p>

            {/* Option selector */}
            <div className="mt-5 space-y-3">
              {/* Bundle - hero offer */}
              <button
                type="button"
                data-testid="hero-option-bundle"
                onClick={() => setOption("bundle")}
                className={`relative w-full rounded-xl border-2 p-4 text-left transition-all ${
                  option === "bundle"
                    ? "border-[#6618D0] bg-[#F3EEFC] shadow-sm"
                    : "border-[#E3DDF7] bg-white hover:border-[#C8B2F7]"
                }`}
              >
                <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-[#990EE5] px-2.5 py-0.5 text-[10px] font-extrabold tracking-wide text-white">
                  <Sparkles className="h-3 w-3" /> RECOMMENDED
                </span>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif-display text-[15px] font-bold text-[#6618D0]">
                      Book + Workbook
                    </div>
                    <div className="mt-0.5 text-[12px] leading-snug text-[#5a5a5a]">
                      The book, plus the Response-Ready Tool-Kit workbook to
                      practise it in your own home.
                    </div>
                    <div className="mt-1.5 text-[11px] font-bold text-[#990EE5]">
                      Only $10 more · shipped together
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif-display text-xl font-bold text-[#6618D0]">$40</div>
                  </div>
                </div>
              </button>

              {/* Book only */}
              <button
                type="button"
                data-testid="hero-option-book"
                onClick={() => setOption("book")}
                className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
                  option === "book"
                    ? "border-[#6618D0] bg-[#F3EEFC] shadow-sm"
                    : "border-[#E3DDF7] bg-white hover:border-[#C8B2F7]"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif-display text-[15px] font-bold text-[#6618D0]">
                      The Book
                    </div>
                    <div className="mt-0.5 text-[12px] leading-snug text-[#5a5a5a]">
                      The Mother Behind That Child.
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif-display text-xl font-bold text-[#6618D0]">$30</div>
                  </div>
                </div>
              </button>
            </div>

            <p className="mt-4 rounded-lg bg-[#EAE9F6] px-3.5 py-3 text-[12px] leading-relaxed text-[#4a4a4a]">
              The book shows you <em>why</em> this keeps happening. The workbook
              helps you change it, with guided pages for your own triggers,
              boundaries and family moments. If you want things to feel different
              at home, start with the Book + Workbook.{" "}
              <a href="#bundle" data-testid="hero-compare-link" className="font-bold text-[#6618D0] underline">
                Compare
              </a>
            </p>

            <form onSubmit={handleSubmit} className="mt-5 space-y-3">
              <div className="flex items-center gap-3 rounded-md border border-[#dcdcdc] px-3.5 py-3 focus-within:border-[#6618D0]">
                <User className="h-4 w-4 text-[#9aa0a8]" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="hero-name-input"
                  placeholder="Your full name"
                  className="w-full bg-transparent text-sm text-[#2a2a2a] outline-none placeholder:text-[#9aa0a8]"
                />
              </div>
              <div className="flex items-center gap-3 rounded-md border border-[#dcdcdc] px-3.5 py-3 focus-within:border-[#6618D0]">
                <Mail className="h-4 w-4 text-[#9aa0a8]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="hero-email-input"
                  placeholder="Your email address"
                  className="w-full bg-transparent text-sm text-[#2a2a2a] outline-none placeholder:text-[#9aa0a8]"
                />
              </div>

              {/* Quantity selector */}
              <div className="flex items-center justify-between rounded-md border border-[#dcdcdc] px-3.5 py-2.5">
                <span className="text-sm font-semibold text-[#2a2a2a]">Quantity</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAE9F6] text-[#6618D0] transition-colors hover:bg-[#C8B2F7] disabled:opacity-40"
                    disabled={quantity <= 1}
                    aria-label="Decrease quantity"
                    data-testid="hero-qty-decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span data-testid="hero-qty-value" className="w-5 text-center text-sm font-bold text-[#1E1E1E]">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(100, q + 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EAE9F6] text-[#6618D0] transition-colors hover:bg-[#C8B2F7]"
                    aria-label="Increase quantity"
                    data-testid="hero-qty-increase"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between rounded-md bg-[#EAE9F6] px-3.5 py-3">
                <span className="text-sm font-semibold text-[#4a4a4a]">
                  Total <span className="text-[#9aa0a8]">(${unitPrice}.00 each)</span>
                </span>
                <span data-testid="hero-total" className="font-serif-display text-xl font-bold text-[#6618D0]">${total}</span>
              </div>

              <button
                type="submit"
                data-testid="hero-submit-button"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-[#6618D0] px-6 py-3.5 font-serif-display text-sm font-bold tracking-wide text-white shadow-sm transition-all hover:bg-[#5512b0] hover:shadow-md active:scale-[0.99] disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> REDIRECTING…
                  </>
                ) : (
                  `PREORDER NOW — $${total}`
                )}
              </button>
            </form>

            <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#8a8a8a]">
              <Lock className="h-3 w-3" />
              Secure checkout powered by Stripe. Shipping details collected at checkout.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
